import { useMutation } from '@apollo/client';
import {
    faArrowLeftToLine,
    faArrowRightToLine,
    faBolt,
    faChevronRight,
    faEllipsisVertical,
    faFolder,
    faMagnifyingGlass,
    faPlus,
    faThumbtack,
    faTriangleExclamation,
    faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import Button from '../../core/components/Button.tsx';
import ConfirmModal from '../../core/components/ConfirmModal.tsx';
import DropdownMenu from '../../core/components/DropdownMenu.tsx';
import StringField from '../../core/components/Fields/StringField.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import Tooltip from '../../core/components/Tooltip.tsx';
import { useApplicationContext } from '../../core/contexts/ApplicationContext.tsx';
import classnames from '../../core/utils/classnames.tsx';
import { useLocalStorage } from '../../core/utils/hooks/useLocalStorage.ts';
import { url } from '../../core/utils/link.ts';
import { canReadAllCustomerChats } from '../../users/utils.ts';
import { AUTOMATION_PATH, CHAT_PATH, CONVERSATION_PATH } from '../constants.ts';
import { graphqlErrorMessage } from '../errors.ts';
import {
    DELETE_CONVERSATION,
    GET_CONVERSATION_SCHEDULES,
    GET_CONVERSATIONS,
    SET_CONVERSATION_PINNED,
} from '../queries.ts';
import { formatRelativeDay } from '../utils/relativeDate.ts';
import {
    DeleteMenuIcon,
    dangerItemClassName,
    dangerMenuIconClassName,
    itemClassName,
    menuIconClassName,
} from './automations/AutomationCard.tsx';
import SidebarSearchResults from './automations/SidebarSearchResults.tsx';
import { ChatUsageIndicator } from './ChatUsageIndicator.tsx';
import { displayName } from './ProjectSharingFields.tsx';
import { formatTimestamp } from './toolDisplay.tsx';

const PlusIcon = withIcon(faPlus);
const CollapseIcon = withIcon(faArrowLeftToLine);
const ExpandIcon = withIcon(faArrowRightToLine);
const CloseIcon = withIcon(faXmark);
const BoltIcon = withIcon(faBolt);
const ProjectsIcon = withIcon(faFolder);
const SearchIcon = withIcon(faMagnifyingGlass);
const PinIcon = withIcon(faThumbtack);
const EllipsisIcon = withIcon(faEllipsisVertical);
const SectionToggleIcon = withIcon(faChevronRight);

const sectionLabel = 'px-2 pb-1 pt-3 text-xs font-medium tracking-wide text-content-medium';

// "Shared with me" and "Recent" fold away, chevron after the label as in the design; Pinned and Scheduled stay plain
const CollapsibleSectionHeader = ({
    label,
    collapsed,
    onToggle,
}: {
    label: string;
    collapsed: boolean;
    onToggle: () => void;
}) => (
    <button
        type="button"
        aria-expanded={!collapsed}
        onClick={onToggle}
        className={classnames(
            sectionLabel,
            'flex w-full cursor-pointer items-center gap-1.5 border-none bg-transparent text-left hover:text-content-medium'
        )}
    >
        {label}
        <SectionToggleIcon className={classnames('text-xs transition-transform', !collapsed && 'rotate-90')} />
    </button>
);
const WarningIcon = withIcon(faTriangleExclamation);

const SidebarNavRow = React.forwardRef<
    HTMLButtonElement,
    { icon: React.ReactNode; label: string; active: boolean; onClick: () => void; warning?: string | null }
>(({ icon, label, active, onClick, warning = null, ...props }, ref) => (
    <button
        ref={ref}
        type="button"
        aria-current={active ? 'page' : undefined}
        className={classnames(
            'flex w-full cursor-pointer items-center gap-3 rounded-lg border-none px-3 py-2 text-left text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-brand-strong',
            active
                ? 'bg-surface-brand-medium-default text-content-brand-strong'
                : 'bg-surface-transparent text-content-strong hover:bg-surface-medium'
        )}
        onClick={onClick}
        {...props}
    >
        <span className="flex w-4 flex-none justify-center">{icon}</span>
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {warning && (
            <Tooltip content={warning}>
                <span role="img" aria-label={warning} className="flex-none text-content-danger-strong">
                    <WarningIcon />
                </span>
            </Tooltip>
        )}
    </button>
));
SidebarNavRow.displayName = 'SidebarNavRow';

const shareRecipientCount = (shares: any[] = []) => {
    const teams = new Set<string>();
    const members = new Set<string>();
    shares.forEach((share) => {
        if (share.team) teams.add(share.team.id);
        (share.members || []).forEach((member) => members.add(member.id));
    });
    return teams.size + members.size;
};

const ConversationListItem = ({ conversation, title = null, active, onTogglePin, showCreator = false }) => {
    const navigate = useNavigate();
    const { t } = useTranslation('chat');
    const { publishToast } = useToaster();

    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [deleteConversation, { loading: deleting }] = useMutation(DELETE_CONVERSATION, {
        refetchQueries: [{ query: GET_CONVERSATIONS }, { query: GET_CONVERSATION_SCHEDULES }],
    });

    const handleDelete = async () => {
        try {
            await deleteConversation({ variables: { id: conversation.id } });
            setConfirmingDelete(false);
            if (active) navigate(url(CHAT_PATH));
        } catch (e) {
            console.error(e);
            publishToast({ description: graphqlErrorMessage(e, t('thread.deleteError')), status: 'error' });
        }
    };

    const recipientCount = shareRecipientCount(conversation.shares);
    const lastActive = formatRelativeDay(conversation.updatedAt || conversation.createdAt, t);
    const creator =
        showCreator && conversation.createdBy
            ? t('sidebar.sharedFrom', { name: displayName(conversation.createdBy) })
            : null;
    const metaParts = [
        creator,
        recipientCount > 0 ? t('sidebar.sharedWith', { count: recipientCount }) : null,
        lastActive,
    ];
    // another user's chat (visible to support): read-only, so no pin/delete actions
    const ownChat = conversation.isOwner !== false;

    return (
        <div
            className={classnames(
                'group relative flex items-center gap-2 rounded px-3 py-2 transition-colors',
                active ? 'bg-surface-brand-weak' : 'hover:bg-surface-strong focus-within:bg-surface-strong'
            )}
        >
            <Link
                to={url(CONVERSATION_PATH, { conversationId: conversation.id })}
                aria-current={active ? 'page' : undefined}
                className="flex min-w-0 flex-1 flex-col gap-0.5 rounded after:absolute after:inset-0 after:rounded focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-line-brand-strong"
            >
                <span className="truncate text-sm font-medium text-content-strong">
                    {title || conversation.title || t('sidebar.untitled')}
                    {conversation.pinned && <span className="sr-only"> · {t('sidebar.pinnedChat')}</span>}
                </span>
                <span className="truncate text-xs text-content-medium">{metaParts.filter(Boolean).join(' · ')}</span>
            </Link>
            <div className="relative z-10 flex h-8 w-8 flex-none items-center justify-center">
                {conversation.pinned && (
                    <PinIcon className="text-content-medium transition-opacity group-focus-within:opacity-0 group-hover:opacity-0" />
                )}
                {ownChat && (
                    <DropdownMenu>
                        <DropdownMenu.Trigger asChild>
                            <Button
                                type="button"
                                variant="ghost"
                                aria-label={t('sidebar.chatActions')}
                                className="absolute inset-0 flex items-center justify-center px-0 py-0 opacity-0 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-line-brand-strong group-hover:opacity-100 data-[state=open]:opacity-100"
                            >
                                <EllipsisIcon />
                            </Button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Content align="start" side="right">
                            <DropdownMenu.Item className={itemClassName} onClick={onTogglePin}>
                                <PinIcon className={menuIconClassName} />
                                {conversation.pinned ? t('sidebar.unpin') : t('sidebar.pin')}
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                                className={dangerItemClassName}
                                onClick={() => setConfirmingDelete(true)}
                            >
                                <DeleteMenuIcon className={dangerMenuIconClassName} />
                                {t('sidebar.delete')}
                            </DropdownMenu.Item>
                        </DropdownMenu.Content>
                    </DropdownMenu>
                )}
            </div>
            {confirmingDelete && (
                <ConfirmModal
                    title={t('sidebar.deleteConfirm.title')}
                    labelConfirm={t('sidebar.deleteConfirm.yes')}
                    confirmDisabled={deleting}
                    danger
                    onConfirm={handleDelete}
                    onCancel={() => setConfirmingDelete(false)}
                >
                    {t('sidebar.deleteConfirm.text', { name: title || conversation.title || t('sidebar.untitled') })}
                </ConfirmModal>
            )}
        </div>
    );
};

/** A snapshot someone shared with the viewer (ENG-11386): title, who shared it and when. Opens read-only. */
const SharedConversationListItem = ({ share, active, onSelect }) => {
    const { t } = useTranslation('chat');
    return (
        <div
            className={classnames(
                'flex cursor-pointer flex-col gap-0.5 rounded-lg px-2.5 py-2',
                active ? 'bg-surface-brand-medium-default' : 'hover:bg-surface-medium'
            )}
            onClick={onSelect}
        >
            <span className="truncate text-sm font-medium text-content-strong">
                {share.snapshot.title || t('sidebar.sharedUntitled')}
            </span>
            <span className="truncate text-xs text-content-weak">
                {t('sidebar.sharedFrom', { name: share.sharedBy ? displayName(share.sharedBy) : t('projects.admin') })}
            </span>
            <span className="truncate text-xs text-content-weak">
                {t('sidebar.sharedOn')} {formatTimestamp(share.createdAt)}
            </span>
        </div>
    );
};

/** A chat receiving someone's scheduled runs (ENG-11387): the viewer's own living chat, listed with the shares. */
const DeliveredConversationListItem = ({ conversation, active, onSelect }) => {
    const { t } = useTranslation('chat');
    const { sender } = conversation.scheduledDelivery;
    return (
        <div
            className={classnames(
                'flex cursor-pointer flex-col gap-0.5 rounded-lg px-2.5 py-2',
                active ? 'bg-surface-brand-medium-default' : 'hover:bg-surface-medium'
            )}
            onClick={onSelect}
        >
            <span className="truncate text-sm font-medium text-content-strong">
                {conversation.title || conversation.scheduledDelivery.name}
            </span>
            <span className="truncate text-xs text-content-weak">
                {t('sidebar.sharedFrom', { name: sender ? displayName(sender) : t('projects.admin') })} ·{' '}
                {t('sidebar.onSchedule')}
            </span>
        </div>
    );
};

export const ConversationSidebar = ({
    conversations,
    // snapshots shared with the viewer (ENG-11386)
    sharedConversations = [],
    activeId,
    scheduledConversationIds = new Set<string>(),
    schedules = [],
    triggers = [],
    onOpenTrigger = null,
    onOpenProjects = null,
    projectsActive = false,
    onOpenAutomations = null,
    automationsActive = false,
    // a schedule or email automation needs attention (failed last run, paused after failures)
    automationsWarning = false,
    canStartConversation = true,
    onClose = null,
}) => {
    const { t } = useTranslation('chat');
    const { publishToast } = useToaster();
    const { user } = useApplicationContext();
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [collapsed, setCollapsed] = useLocalStorage('chat.sidebarCollapsed', false);
    const [foldedSections, setFoldedSections] = useLocalStorage('chat.sidebarFoldedSections', {});

    const toggleSection = (key: string) => setFoldedSections({ ...foldedSections, [key]: !foldedSections[key] });

    const [setPinned] = useMutation(SET_CONVERSATION_PINNED, {
        onError: (error) =>
            publishToast({ description: graphqlErrorMessage(error, t('sidebar.pinError')), status: 'error' }),
    });

    const isSearching = search.trim() !== '';
    const pinned = conversations.filter((conversation) => conversation.pinned);
    // chats receiving someone's scheduled runs sit with the shares, not in Recent (ENG-11387)
    const delivered = conversations.filter((conversation) => !conversation.pinned && conversation.scheduledDelivery);
    const scheduled = conversations.filter(
        (conversation) =>
            !conversation.pinned && !conversation.scheduledDelivery && scheduledConversationIds.has(conversation.id)
    );
    const recent = conversations.filter(
        (conversation) =>
            !conversation.pinned && !conversation.scheduledDelivery && !scheduledConversationIds.has(conversation.id)
    );

    const scheduleNames = new Map<string, string>();
    schedules.forEach((schedule) => {
        if (schedule.conversationId && schedule.name && !scheduleNames.has(schedule.conversationId)) {
            scheduleNames.set(schedule.conversationId, schedule.name);
        }
    });

    // support and internal users get every chat of the customer listed; label whose chat each row is
    const showCreators = canReadAllCustomerChats(user);
    const renderConversation = (conversation) => (
        <ConversationListItem
            key={conversation.id}
            conversation={conversation}
            title={scheduleNames.get(conversation.id)}
            active={conversation.id === activeId}
            onTogglePin={() => setPinned({ variables: { id: conversation.id, pinned: !conversation.pinned } })}
            showCreator={showCreators && conversation.isOwner === false}
        />
    );

    // inside the mobile drawer (which passes onClose) the collapsed rail makes no sense
    if (collapsed && !onClose) {
        return (
            <div className="flex w-14 flex-none flex-col items-center gap-2 border-0 border-r border-solid border-line-weak bg-surface-weak px-2 pb-4 pt-4">
                <button
                    type="button"
                    title={t('sidebar.expand')}
                    aria-label={t('sidebar.expand')}
                    className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent text-content-weak hover:bg-surface-strong hover:text-content-strong"
                    onClick={() => setCollapsed(false)}
                >
                    <ExpandIcon />
                </button>
                {canStartConversation ? (
                    <Link
                        to={url(CHAT_PATH)}
                        title={t('sidebar.newConversation')}
                        className="flex h-8 w-8 items-center justify-center rounded-lg bg-surface-brand-strong-default text-content-white hover:bg-surface-brand-strong-hover"
                    >
                        <PlusIcon />
                    </Link>
                ) : (
                    <span
                        title={t('sidebar.newConversation')}
                        className="flex h-8 w-8 items-center justify-center rounded-lg bg-surface-brand-strong-default text-content-white opacity-50"
                    >
                        <PlusIcon />
                    </span>
                )}
                {onOpenProjects && (
                    <Button
                        variant="outline"
                        active={projectsActive}
                        title={t('sidebar.projects')}
                        aria-label={t('sidebar.projects')}
                        className="h-8 w-8 px-0 text-center"
                        onClick={() => onOpenProjects()}
                    >
                        <ProjectsIcon />
                    </Button>
                )}
                {onOpenAutomations && (
                    <Button
                        variant="outline"
                        active={automationsActive}
                        title={t('automations.title')}
                        aria-label={
                            automationsWarning
                                ? `${t('automations.title')} · ${t('automations.sidebar.runFailed')}`
                                : t('automations.title')
                        }
                        className="relative h-8 w-8 px-0 text-center"
                        onClick={() => onOpenAutomations()}
                    >
                        <BoltIcon />
                        {automationsWarning && (
                            <span
                                aria-hidden
                                className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-surface-danger-strong-default"
                            />
                        )}
                    </Button>
                )}
            </div>
        );
    }

    return (
        <div className="flex w-72 flex-none flex-col border-0 border-r border-solid border-line-weak bg-surface-weak">
            <div className="flex items-center gap-2 px-4 pb-2 pt-4">
                <h1 className="m-0 text-base font-semibold text-content-strong">{t('title')}</h1>
                <button
                    type="button"
                    title={onClose ? t('sidebar.close') : t('sidebar.collapse')}
                    aria-label={onClose ? t('sidebar.close') : t('sidebar.collapse')}
                    className="ml-auto flex h-7 w-7 flex-none cursor-pointer items-center justify-center rounded-lg border-none bg-transparent text-content-weak hover:bg-surface-strong hover:text-content-strong"
                    onClick={onClose ?? (() => setCollapsed(true))}
                >
                    {onClose ? <CloseIcon /> : <CollapseIcon />}
                </button>
            </div>
            <div className="px-4 pb-2">
                {canStartConversation ? (
                    // a real link so cmd/ctrl-click opens the new conversation in a new tab
                    <Button variant="primary" className="block w-full text-center" asChild>
                        <Link to={url(CHAT_PATH)}>
                            <PlusIcon className="mr-1.5" />
                            {t('sidebar.newConversation')}
                        </Link>
                    </Button>
                ) : (
                    // no agent configured yet — nothing to start a conversation with
                    <Button variant="primary" className="block w-full text-center" disabled>
                        <PlusIcon className="mr-1.5" />
                        {t('sidebar.newConversation')}
                    </Button>
                )}
            </div>
            <div className="px-4 pb-3">
                <StringField
                    type="search"
                    prefix={<SearchIcon />}
                    aria-label={t('sidebar.searchLabel')}
                    placeholder={t('sidebar.searchPlaceholder')}
                    value={search}
                    onValueChange={setSearch}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') setSearch('');
                    }}
                />
            </div>
            {(onOpenProjects || onOpenAutomations) && (
                // Projects and Automations as nav rows under the search (design)
                <nav className="flex flex-col gap-0.5 px-4 pb-2">
                    {onOpenProjects && (
                        <SidebarNavRow
                            icon={<ProjectsIcon />}
                            label={t('sidebar.projects')}
                            active={projectsActive}
                            onClick={() => onOpenProjects()}
                        />
                    )}
                    {onOpenAutomations && (
                        <SidebarNavRow
                            icon={<BoltIcon />}
                            label={t('automations.title')}
                            active={automationsActive}
                            onClick={() => onOpenAutomations()}
                            warning={automationsWarning ? t('automations.sidebar.runFailed') : null}
                        />
                    )}
                </nav>
            )}
            <div className="flex-1 overflow-y-auto px-2 pb-4">
                {isSearching ? (
                    <SidebarSearchResults
                        query={search}
                        conversations={conversations}
                        sharedConversations={sharedConversations}
                        schedules={schedules}
                        triggers={onOpenTrigger ? triggers : []}
                        activeId={activeId}
                        onOpenChat={(conversationId) => navigate(url(CONVERSATION_PATH, { conversationId }))}
                        onOpenSchedule={(scheduleId) => navigate(url(AUTOMATION_PATH, { scheduleId }))}
                        onOpenTrigger={(trigger) => onOpenTrigger?.(trigger)}
                    />
                ) : (
                    <>
                        {scheduled.length > 0 && (
                            <>
                                <CollapsibleSectionHeader
                                    label={t('sidebar.scheduled')}
                                    collapsed={!!foldedSections.scheduled}
                                    onToggle={() => toggleSection('scheduled')}
                                />
                                {!foldedSections.scheduled && scheduled.map(renderConversation)}
                            </>
                        )}
                        {(sharedConversations.length > 0 || delivered.length > 0) && (
                            <>
                                <CollapsibleSectionHeader
                                    label={t('sidebar.sharedWithMe')}
                                    collapsed={!!foldedSections.shared}
                                    onToggle={() => toggleSection('shared')}
                                />
                                {!foldedSections.shared &&
                                    sharedConversations.map((share) => (
                                        <SharedConversationListItem
                                            key={share.id}
                                            share={share}
                                            active={share.snapshot.id === activeId}
                                            onSelect={() =>
                                                navigate(url(CONVERSATION_PATH, { conversationId: share.snapshot.id }))
                                            }
                                        />
                                    ))}
                                {!foldedSections.shared &&
                                    delivered.map((conversation) => (
                                        <DeliveredConversationListItem
                                            key={conversation.id}
                                            conversation={conversation}
                                            active={conversation.id === activeId}
                                            onSelect={() =>
                                                navigate(url(CONVERSATION_PATH, { conversationId: conversation.id }))
                                            }
                                        />
                                    ))}
                            </>
                        )}
                        {pinned.length > 0 && (
                            <>
                                <div className={sectionLabel}>{t('sidebar.pinned')}</div>
                                {pinned.map(renderConversation)}
                            </>
                        )}
                        {recent.length > 0 && (
                            <CollapsibleSectionHeader
                                label={t('sidebar.recent')}
                                collapsed={!!foldedSections.recent}
                                onToggle={() => toggleSection('recent')}
                            />
                        )}
                        {!foldedSections.recent && recent.map(renderConversation)}
                    </>
                )}
            </div>

            <ChatUsageIndicator conversationId={activeId} />
        </div>
    );
};
